import React, {useEffect, useRef, useState, useMemo} from "react";
import {useLocation, useNavigate, useParams, Link} from "react-router-dom";
import axios from "axios";
import {Breadcrumb, Modal, Progress, Tooltip, message, Button, Slider, Switch} from "antd";
import {useCookies} from "react-cookie";
import dayjsInstance from "../utils/dayjs";
import SpeechRecognition, {useSpeechRecognition} from 'react-speech-recognition';
import {
    AudioFilled, AudioMutedOutlined, AudioOutlined, HomeOutlined,
    StepBackwardOutlined, StepForwardOutlined, SoundOutlined,
    TrophyOutlined, FireOutlined, CheckCircleOutlined, VideoCameraOutlined,
    LoadingOutlined, WarningOutlined, PlayCircleFilled, PauseCircleFilled,
    DownloadOutlined, SettingOutlined, FontSizeOutlined, CloseCircleOutlined,
    EyeOutlined, RetweetOutlined
} from "@ant-design/icons";
import toCamelCase from "../utils/toCamelCase";
import toLowerCamelCase from "../utils/toLowerCamelCase";
import { alignTranscript } from "../utils/alignTranscript";
import convertTimeToMilliseconds from "../utils/convertTimeToMilliseconds";

export default function Speaking() {
    const {slug} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const index = location?.state?.index ?? 0;
    const containerRef = useRef(null);

    const [dataLesson, setdataLesson] = useState([]);
    const [options, setOptions] = useState([]);
    const [records, setRecords] = useState([]);
    const [recordMain, setRecordMain] = useState([]);
    const [arrayMain, setArrayMain] = useState([]);
    const [indexLyric, setIndexLyric] = useState(0);
    const [recordingIndex, setRecordingIndex] = useState(-1);
    const [story, setStory] = useState("Main Story");
    const [recordingAll, setRecordingAll] = useState(false);
    const [isScoringAll, setIsScoringAll] = useState(false); // true while batch-scoring after stop
    const [isModalSpeakOpen, setIsModalSpeakOpen] = useState(false);
    const [readingSpeed, setReadingSpeed] = useState(1); // multiplier: 0.5 = slow, 1 = normal, 1.5 = fast
    const [dataUserTopic, setDataUserTopic] = useState({});
    const [cookies] = useCookies(["user"]);
    const [isRecording, setIsRecording] = useState(false);
    const [cameraReady, setCameraReady] = useState(false); // Camera prepared but not recording yet
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [micPermission, setMicPermission] = useState('unknown'); // 'unknown' | 'granted' | 'denied' | 'unsupported'
    const [timestamps, setTimestamps] = useState({});     // LRC timestamps per track
    const [audioCurrentTime, setAudioCurrentTime] = useState(0); // ms
    const [audioIsPlaying, setAudioIsPlaying] = useState(false);
    const [showScoreModal, setShowScoreModal] = useState(false);
    const [scoreResult, setScoreResult] = useState(null); // { avgPoint, lines, totalLines }
    const [showSettings, setShowSettings] = useState(false);
    const [subtitleFontSize, setSubtitleFontSize] = useState(16);
    const [translationFontSize, setTranslationFontSize] = useState(14);
    const [showTranslation, setShowTranslation] = useState(true);
    const [showIPA, setShowIPA] = useState(true);
    const [autoScroll, setAutoScroll] = useState(true);
    const [autoTranscript, setAutoTranscript] = useState(false); // Auto-record each sentence
    const [userInfo, setUserInfo] = useState(null); // fresh user data from API (vip_expire_at)

    const [translations, setTranslations] = useState([]);
    const [ipaData, setIpaData] = useState([]);
    const [translationLoading, setTranslationLoading] = useState(false);

    // Hidden audio element for camera-recording mode
    const audioElemRef = useRef(null);

    const { transcript, interimTranscript, listening, resetTranscript } = useSpeechRecognition();

    // Check browser support and request mic permission on mount
    useEffect(() => {
        if (!SpeechRecognition.browserSupportsSpeechRecognition()) {
            setMicPermission('unsupported');
            return;
        }
        // Request mic permission eagerly so user sees the prompt right away
        navigator.mediaDevices?.getUserMedia({ audio: true })
            .then(stream => {
                stream.getTracks().forEach(t => t.stop()); // release immediately
                setMicPermission('granted');
            })
            .catch(err => {
                if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                    setMicPermission('denied');
                    message.error('Microphone bị từ chối. Vào Cài đặt trình duyệt → Quyền riêng tư → Microphone → Cho phép rồi tải lại trang.');
                } else {
                    setMicPermission('granted'); // device may still work
                }
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refs so the listening-change effect can always read latest values
    // without those values being in the dependency array (which would re-fire the effect)
    const recordingAllRef = useRef(recordingAll);
    const fullTranscriptRef = useRef(''); // accumulate per-sentence transcripts during recordingAll
    const indexLyricRef   = useRef(indexLyric);
    const arrayMainRef    = useRef(arrayMain);
    const transcriptRef   = useRef(transcript);
    const dataLessonRef   = useRef(dataLesson);
    const storyRef        = useRef(story);
    const clickMicRef     = useRef(null); // filled after clickMic is defined
    const userStoppedRef  = useRef(false); // true when user manually pressed stop (no auto-advance)
    const hasStartedRef   = useRef(false); // true once SpeechRecognition has started at least once
    const autoAdvanceTimerRef = useRef(null); // holds the setTimeout id for lyric auto-advance
    const readingSpeedRef = useRef(readingSpeed);
    const autoTranscriptRef = useRef(autoTranscript);
    useEffect(() => { readingSpeedRef.current = readingSpeed; }, [readingSpeed]);
    useEffect(() => { autoTranscriptRef.current = autoTranscript; }, [autoTranscript]);

    // Fetch translations when toggled on
    useEffect(() => {
        if (showTranslation && arrayMain?.length > 0 && translations.length === 0) {
            fetchTranslations();
        }
    }, [showTranslation, arrayMain]);

    // Generate IPA when toggled on
    useEffect(() => {
        if (showIPA && arrayMain?.length > 0 && ipaData.length === 0) {
            const ipa = arrayMain.map(line => textToIPA(line || ''));
            setIpaData(ipa);
        }
    }, [showIPA, arrayMain]);

    const fetchTranslations = async () => {
        if (!arrayMain || arrayMain.length === 0) return;
        setTranslationLoading(true);
        try {
            const payload = { lines: arrayMain, from: 'en', to: 'vi' };
            const result = await axios.post(`${process.env.REACT_APP_API_URL}/translation/batch`, payload, { timeout: 30000 });

            if (result?.data?.translations && Array.isArray(result.data.translations) && result.data.translations.length === arrayMain.length) {
                const backendArr = result.data.translations;
                const hasNonEmpty = backendArr.some(t => typeof t === 'string' && t.trim().length > 0);
                if (hasNonEmpty) {
                    setTranslations(backendArr);
                } else {
                    // fallback to per-line LibreTranslate when backend returns empty strings
                    try {
                        const perLine = await Promise.all(arrayMain.map(async (ln) => {
                            try {
                                const r = await axios.post('https://libretranslate.de/translate', { q: ln || '', source: 'en', target: 'vi', format: 'text' }, { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' }, timeout: 20000 });
                                return r.data?.translatedText || '';
                            } catch (inner) { console.error('Libre fallback line error', inner); return ''; }
                        }));
                        setTranslations(perLine);
                    } catch (inner) { console.error('Libre fallback failed', inner); setTranslations([]); }
                }
            } else {
                // backend returned invalid structure or length mismatch → try LibreTranslate per-line
                try {
                    const perLine = await Promise.all(arrayMain.map(async (ln) => {
                        try {
                            const r = await axios.post('https://libretranslate.de/translate', { q: ln || '', source: 'en', target: 'vi', format: 'text' }, { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' }, timeout: 20000 });
                            return r.data?.translatedText || '';
                        } catch (inner) { console.error('Libre fallback line error', inner); return ''; }
                    }));
                    setTranslations(perLine);
                } catch (inner) { console.error('Libre fallback failed after invalid backend response', inner); setTranslations([]); }
            }
        } catch (e) {
            console.error('Translation error:', e);
            setTranslations([]);
            // attempt fallback
            try {
                const perLine = await Promise.all(arrayMain.map(async (ln) => {
                    try {
                        const r = await axios.post('https://libretranslate.de/translate', { q: ln || '', source: 'en', target: 'vi', format: 'text' }, { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' }, timeout: 20000 });
                        return r.data?.translatedText || '';
                    } catch (inner) { console.error('Libre fallback line error', inner); return ''; }
                }));
                setTranslations(perLine);
            } catch (libErr) { console.error('LibreTranslate fallback failed:', libErr); }
        } finally {
            setTranslationLoading(false);
        }
    };

    const textToIPA = (text) => {
        if (!text) return '';
        const words = text.replace(/[^a-zA-Z\s']/g, '').toLowerCase().split(/\s+/);
        return '/' + words.map(w => wordToIPA(w)).join(' ') + '/';
    };

    const wordToIPA = (word) => {
        if (!word) return '';
        const map = [
            [/tion$/i, 'ʃən'], [/sion$/i, 'ʒən'], [/ous$/i, 'əs'], [/ture$/i, 'tʃɚ'],
            [/ing$/i, 'ɪŋ'], [/ed$/i, 'd'], [/th/gi, 'θ'], [/ch/gi, 'tʃ'], [/sh/gi, 'ʃ'],
            [/oo/gi, 'u'], [/ee/gi, 'i'], [/ea/gi, 'i'], [/a/gi, 'æ'], [/e$/gi, ''],
        ];
        let ipa = word;
        for (const [pat, repl] of map) ipa = ipa.replace(pat, repl);
        return ipa || word;
    };

    useEffect(() => { recordingAllRef.current = recordingAll; }, [recordingAll]);
    useEffect(() => { indexLyricRef.current   = indexLyric;   }, [indexLyric]);
    useEffect(() => { arrayMainRef.current    = arrayMain;    }, [arrayMain]);
    useEffect(() => { transcriptRef.current   = transcript;   }, [transcript]);
    useEffect(() => { dataLessonRef.current   = dataLesson;   }, [dataLesson]);
    useEffect(() => { storyRef.current        = story;        }, [story]);

    const convertString = (track) => {
        if (track && typeof track === 'string') {
            const data = track.split('\n').map(el => {
                const closeBracketIndex = el.indexOf(']');
                return closeBracketIndex >= 0 ? el.slice(closeBracketIndex + 1) : el;
            }).filter(line => line.trim()); // Remove empty lines
            setArrayMain(data);
        } else {
            setArrayMain([]);
        }
    };

    /** Per-sentence mic (individual recording, continuous: false) */
    const clickMic = async (i) => {
        if (micPermission === 'unsupported') {
            message.error('Trình duyệt này không hỗ trợ nhận dạng giọng nói. Vui lòng dùng Chrome hoặc Edge.');
            return;
        }
        if (micPermission === 'denied') {
            message.error('Microphone bị từ chối. Vào Cài đặt trình duyệt → Microphone → Cho phép rồi tải lại trang.');
            return;
        }
        userStoppedRef.current = false;
        setIndexLyric(i);
        setRecordingIndex(i);
        try { SpeechRecognition.getRecognition().abort(); } catch (e) {}
        // reset per-sentence transcript buffer; we'll append to fullTranscriptRef on stop
        resetTranscript();
        try {
            await SpeechRecognition.startListening({ language: 'en-US', continuous: false });
            hasStartedRef.current = true;
        } catch (err) {
            console.error('startListening error:', err);
            if (err.name === 'not-allowed' || err.message?.includes('not-allowed')) {
                setMicPermission('denied');
                message.error('Không thể truy cập microphone. Kiểm tra quyền trình duyệt và thử lại.');
            } else {
                message.warning('Không thể bắt đầu ghi âm. Thử lại sau giây lát.');
            }
            setRecordingIndex(-1);
        }
    };
    // Keep a stable ref so the listening effect can call clickMic without stale closure
    clickMicRef.current = clickMic;

    const stopMic = async () => {
        userStoppedRef.current = true;
        setRecordingIndex(-1);
        await SpeechRecognition.stopListening();
        // If in continuous recording mode, append the last sentence transcript to accumulator
        try {
            if (recordingAllRef.current) {
                const t = transcriptRef.current?.trim();
                if (t) {
                    fullTranscriptRef.current = (fullTranscriptRef.current ? fullTranscriptRef.current + ' ' : '') + t;
                }
                // clear per-sentence transcript after appending
                resetTranscript();
            }
        } catch (e) { console.error('stopMic append error', e); }
    };

    // Per-sentence record handler that supports camera (video) + audio
    const handlePerSentenceRecord = async (i) => {
        try {
            // If camera is ready, use per-sentence SpeechRecognition while leaving
            // the MediaRecorder running for the full session.
            if (cameraReady) {
                // If currently recording this sentence -> stop recognition and score
                if (recordingIndex === i) {
                    // stop speech recog and then score the recorded sentence
                    await stopMic();
                    setRecordingIndex(-1);
                    const spoken = transcriptRef.current?.trim();
                    if (spoken) await handleRecord(spoken, i);
                    return;
                }

                // Start per-sentence speech recognition (do NOT start/stop MediaRecorder here)
                try {
                    if (!mediaRecorder) {
                        message.error('Camera chưa sẵn sàng để ghi hình');
                        return;
                    }
                    // reset transcript buffer for the sentence
                    resetTranscript();
                    fullTranscriptRef.current = '';
                    setRecordingIndex(i);
                    // start speech recognition for this sentence
                    if (SpeechRecognition.browserSupportsSpeechRecognition()) {
                        try { await SpeechRecognition.startListening({ language: 'en-US', continuous: false }); hasStartedRef.current = true; }
                        catch (err) { console.error('Speech start listening (video) error', err); }
                    }
                } catch (err) {
                    console.error('start per-sentence video record error', err);
                    message.error('Không thể bắt đầu ghi hình từng câu');
                }
                return;
            }

            // Fallback: normal per-sentence audio recording
            if (recordingIndex === -1) {
                await clickMic(i);
            } else {
                await stopMic();
            }
        } catch (e) {
            console.error('handlePerSentenceRecord error', e);
        }
    };

    /** Start continuous recording for the whole lesson
     *  Changed: do NOT start a single continuous SpeechRecognition session.
     *  Instead, run per-sentence recording (call `clickMic(i)`) for each sentence
     *  as it is auto-advanced. This avoids the noisy continuous transcript.
     */
    const startContinuousRecording = async () => {
        if (micPermission === 'unsupported') {
            message.error('Trình duyệt này không hỗ trợ nhận dạng giọng nói. Vui lòng dùng Chrome hoặc Edge.');
            return;
        }
        if (micPermission === 'denied') {
            message.error('Microphone bị từ chối. Vui lòng vào Cài đặt trình duyệt → Microphone → Cho phép rồi tải lại trang.');
            return;
        }
        userStoppedRef.current = false;
        // start fresh
        resetTranscript();
        fullTranscriptRef.current = '';
        hasStartedRef.current = false;
        setRecordingAll(true);
        setRecordingIndex(-1);

        // If camera mode is active and camera stream exists, start recorder on camera stream
        if (cameraReady && cameraStreamRef.current) {
            try {
                if (!mediaRecorder) {
                    const rec = createRecorderForStream(cameraStreamRef.current);
                    setMediaRecorder(rec);
                    rec.start();
                } else if (mediaRecorder.state !== 'recording') {
                    mediaRecorder.start();
                }
            } catch (err) {
                console.error('Error starting camera mediaRecorder for continuous recording', err);
            }
            // Visual auto-advance for camera whole-lesson recording
            scheduleAutoAdvance(0);
            return;
        }

        // Mic-only flow: request audio stream and start MediaRecorder for the audio
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            micStreamRef.current = stream;
            try {
                const rec = createRecorderForStream(stream);
                setMediaRecorder(rec);
                rec.start();
            } catch (err) {
                console.error('Failed to create/start media recorder for mic stream', err);
            }
        } catch (err) {
            console.error('Could not get mic stream for continuous recording', err);
            message.error('Không thể truy cập micro');
            setRecordingAll(false);
            return;
        }

        // Start per-sentence auto-advance+recording from current index
        scheduleAutoAdvancePerSentence(indexLyricRef.current);
    };

    /** Stop continuous recording and batch-score all sentences */
    const stopContinuousRecording = async () => {
        // stop the auto-advance timer and any per-sentence recording in progress
        clearAutoAdvance();

        // Stop any per-sentence speech recognition
        try { await stopMic(); } catch (e) { console.warn(e); }

        // Stop mediaRecorder if present
        try {
            if (mediaRecorder && mediaRecorder.state !== 'inactive') {
                mediaRecorder.stop();
            }
        } catch (err) { console.warn('Error stopping mediaRecorder', err); }

        // Stop mic stream tracks if we created one
        try {
            if (micStreamRef.current) {
                micStreamRef.current.getTracks().forEach(t => t.stop());
                micStreamRef.current = null;
            }
        } catch (err) { console.warn('Error stopping mic stream', err); }

        setRecordingAll(false);
        // scoring happens in the useEffect([listening]) below when transcript accumulates
    };

    /**
     * Schedule auto-advance: highlight each lyric line for (wordCount / wordsPerSecond) seconds
     * then move to the next. Respects readingSpeedRef for speed changes.
     * wordsPerSecond baseline = 1.4 words/sec at 1x (relaxed teaching audio pace)
     */
    const WORDS_PER_SEC_BASE = 1.4;
    const MIN_SENTENCE_MS = 2500; // never less than 2.5s per sentence
    const scheduleAutoAdvance = (startIndex) => {
        clearAutoAdvance(); // clear any existing timer
        const lines = arrayMainRef.current;
        if (!lines || lines.length === 0) return;

        const advanceTo = (idx) => {
            if (idx >= lines.length) return; // reached end
            setIndexLyric(idx);

            const wordCount = (lines[idx] || '').split(/\s+/).filter(Boolean).length || 1;
            const speed = readingSpeedRef.current;
            const durationMs = Math.max(MIN_SENTENCE_MS, (wordCount / (WORDS_PER_SEC_BASE * speed)) * 1000);

            autoAdvanceTimerRef.current = setTimeout(() => {
                if (!recordingAllRef.current) return; // stopped externally
                advanceTo(idx + 1);
            }, durationMs);
        };

        advanceTo(startIndex);
    };

    /** Schedule auto-advance that triggers per-sentence recording (clickMic) for each sentence.
     *  This will call `clickMic(i)` when a sentence becomes active, wait the duration,
     *  then call `stopMic()` and advance to the next sentence. It respects `recordingAllRef`.
     */
    const scheduleAutoAdvancePerSentence = (startIndex) => {
        clearAutoAdvance();
        const lines = arrayMainRef.current;
        if (!lines || lines.length === 0) return;

        const advanceTo = async (idx) => {
            if (idx >= lines.length) return;
            setIndexLyric(idx);

            const wordCount = (lines[idx] || '').split(/\s+/).filter(Boolean).length || 1;
            const speed = readingSpeedRef.current;
            const durationMs = Math.max(MIN_SENTENCE_MS, (wordCount / (WORDS_PER_SEC_BASE * speed)) * 1000);

            // Start per-sentence recording (non-continuous)
            try {
                if (clickMicRef.current) await clickMicRef.current(idx);
            } catch (e) {
                console.error('Per-sentence clickMic error', e);
            }

            autoAdvanceTimerRef.current = setTimeout(async () => {
                // stop per-sentence recording and proceed
                try { await stopMic(); } catch (e) {}
                // allow speech recognition to finalize interim transcript
                await new Promise(res => setTimeout(res, 400));
                if (!recordingAllRef.current) return;
                advanceTo(idx + 1);
            }, durationMs);
        };

        advanceTo(startIndex);
    };

    const clearAutoAdvance = () => {
        if (autoAdvanceTimerRef.current) {
            clearTimeout(autoAdvanceTimerRef.current);
            autoAdvanceTimerRef.current = null;
        }
    };

    /** Score a single sentence against a result text */
    const scoreText = (sentenceWords, resultText) => {
        const resultWords = resultText
            .replace(/[!@#$%^&*(),.?"":{}|<>""]/g, '')
            .toLowerCase()
            .split(/\s+/);
        let matches = 0;
        sentenceWords.forEach(w => {
            const clean = w.replace(/[!@#$%^&*(),.?"":{}|<>""]/g, '').toLowerCase();
            if (resultWords.includes(clean)) matches++;
        });
        return Math.ceil((matches / (sentenceWords.length || 1)) * 100);
    };

    /** POST a single record */
    const postRecord = (lesson_id, position, track, text, point) =>
        axios.post(`${process.env.REACT_APP_API_URL}/record`, {
            lesson_id, position, track, text, point,
        }, {
            headers: { Accept: 'application/json', 'Content-Type': 'application/json',
                Authorization: `Bearer ${cookies?.user?.token}` },
        });

    const handleRecord = async (result, positionOverride) => {
        try {
            const position = positionOverride !== undefined ? positionOverride : indexLyricRef.current;
            const item = arrayMainRef.current[position]?.split(' ') || [];
            const point = scoreText(item, result);
            await postRecord(dataLessonRef.current.id, position, toLowerCamelCase(storyRef.current), result, point);
            await getRecords();
        } catch (error) { console.error(error); }
    };

    /** Skip sentence (mark as skipped with 0 point) */
    const skipSentence = async (i) => {
        try {
            await postRecord(dataLessonRef.current.id, i, toLowerCamelCase(storyRef.current), 'skipped', 0);
            await getRecords();
            message.info('Đã bỏ qua câu này');
            // Auto advance to next sentence
            if (i < arrayMainRef.current.length - 1) {
                setIndexLyric(i + 1);
            }
        } catch (error) {
            console.error(error);
            message.error('Không thể bỏ qua câu này');
        }
    };

    const lesson = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/lesson/getBySlug/${slug}?_t=${Date.now()}`);
            setdataLesson(response.data[0]);
            try { window.dispatchEvent(new Event('page-ready')); } catch (e) {}
        } catch (error) { console.error(error); }
    };

    const getRecords = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/record`, {
                headers: { Accept: 'application/json', 'Content-Type': 'application/json',
                    Authorization: `Bearer ${cookies?.user?.token}` },
            });
            setRecords(response.data);
        } catch (error) { console.error(error); }
    };

    const userTopic = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/usertopic`, {
                headers: { Accept: 'application/json', 'Content-Type': 'application/json',
                    Authorization: `Bearer ${cookies?.user?.token}` },
            });
            setDataUserTopic(response?.data[0]);
        } catch (error) { console.error(error); }
    };

    // ── Audio helpers (for camera-recording mode) ──────────────────────────
    const isAudioUrl = (url) => {
        if (!url || typeof url !== 'string') return false;
        return /\.(mp3|m4a|wav|ogg|webm|aac)(\?.*)?$/i.test(url) || /^data:audio\//.test(url);
    };
    const proxyIfR2 = (u) => {
        if (!u || typeof u !== 'string') return u;
        if (u.includes('.r2.cloudflarestorage.com')) {
            const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
            return `${base}/media/proxy?url=${encodeURIComponent(u)}`;
        }
        return u;
    };
    const resolveAudio = (val) => {
        if (!val) return null;
        if (typeof val === 'string') return isAudioUrl(val) ? val : null;
        if (typeof val === 'object') {
            if (Array.isArray(val) && val.length) {
                const first = val[0];
                if (typeof first === 'string') return isAudioUrl(first) ? first : null;
                if (first?.url && isAudioUrl(first.url)) return first.url;
            }
            if (val.url && isAudioUrl(val.url)) return val.url;
        }
        return null;
    };
    const getAudioSrc = () => {
        const storyKey = storyRef.current;
        const dl = dataLessonRef.current;
        const raw = storyKey === 'Main Story' ? resolveAudio(dl?.mainStoryAudio)
            : storyKey === 'Vocabulary'   ? resolveAudio(dl?.vocabularyAudio)
            : storyKey === 'Mini Story'   ? resolveAudio(dl?.miniStoryAudio)
            : storyKey === 'POV'          ? resolveAudio(dl?.POVAudio)
            : storyKey === 'Comment'      ? resolveAudio(dl?.commentAudio)
            : null;
        return proxyIfR2(raw);
    };
    const downloadAudio = () => {
        const src = getAudioSrc();
        if (!src) { message.error('Không tìm thấy file âm thanh!'); return; }
        const link = document.createElement('a');
        link.href = src;
        link.download = `${dataLessonRef.current?.title || 'audio'}_${(storyRef.current || '').replace(/\s/g, '_')}.mp3`;
        link.target = '_blank';
        link.click();
    };
    const addTimestamps = async () => {
        try {
            const dl = dataLessonRef.current;
            const temp = {};
            const dataKeys = ['mainStory', 'miniStory', 'POV', 'comment', 'vocabulary'];
            for (const key of dataKeys) {
                if (dl?.[key]) {
                    const mapped = key === 'POV' ? 'pOV' : key;
                    try {
                        temp[mapped] = dl[key].split('\n').map(el => {
                            const start = el.indexOf(']');
                            if (start >= 0) {
                                return convertTimeToMilliseconds(el.slice(1, el.indexOf(']')));
                            }
                            return 0;
                        });
                    } catch (e) {
                        temp[mapped] = [];
                    }
                }
            }
            setTimestamps(temp);
        } catch (e) {
            console.error('addTimestamps error', e);
            setTimestamps({});
        }
    };

    // Helper: create a MediaRecorder for a given stream with consistent handlers
    const createRecorderForStream = (stream) => {
        const mimeTypes = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
        let selectedMime = '';
        for (const mime of mimeTypes) { if (MediaRecorder.isTypeSupported(mime)) { selectedMime = mime; break; } }
        const recorder = new MediaRecorder(stream, selectedMime ? { mimeType: selectedMime } : {});
        recorder.chunks = [];
        recorder.onstart = () => { recorder.chunks.length = 0; };
        recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) recorder.chunks.push(e.data); };
        recorder.onstop = async () => {
            try {
                const ext = selectedMime.includes('mp4') ? 'mp4' : 'webm';
                const blob = new Blob(recorder.chunks, { type: selectedMime || 'video/webm' });
                const url = URL.createObjectURL(blob);
                // Offer download
                try {
                    const a = document.createElement('a');
                    a.href = url; a.download = `speaking_${dataLessonRef.current?.title || 'lesson'}.${ext}`;
                    a.click();
                } catch (err) { console.error('Download error', err); }

                // Upload recorded blob to backend so server can store/process it
                try {
                    const fd = new FormData();
                    fd.append('file', blob, `speaking_${dataLessonRef.current?.id || 'lesson'}.${ext}`);
                    const resp = await axios.post(`${process.env.REACT_APP_API_URL}/uploadAudio`, fd, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            Authorization: `Bearer ${cookies?.user?.token}`,
                        },
                        timeout: 60000,
                    });
                    const uploadedUrl = resp?.data;
                    if (uploadedUrl) {
                        try {
                            if (audioElemRef.current) {
                                audioElemRef.current.src = uploadedUrl;
                                audioElemRef.current.load();
                                setAudioIsPlaying(false);
                            }
                        } catch (err) { console.error('Attach uploaded audio failed', err); }
                    } else {
                        // Fallback to local blob URL if server didn't return URL
                        if (audioElemRef.current) {
                            audioElemRef.current.src = url;
                            audioElemRef.current.load();
                            setAudioIsPlaying(false);
                        }
                    }
                    message.success('Đã lưu và tải lên bản ghi!');
                } catch (upErr) {
                    console.error('Upload recorded blob failed', upErr);
                    // fallback: attach local blob
                    try { if (audioElemRef.current) { audioElemRef.current.src = url; audioElemRef.current.load(); setAudioIsPlaying(false); } } catch (err) { console.error(err); }
                    message.warn('Bản ghi đã được lưu cục bộ. Upload lên server thất bại.');
                }
            } catch (err) {
                console.error('Error creating video blob:', err);
                message.error('Không thể lưu bản ghi hình');
            }
        };
        return recorder;
    };

    // Rebuild timestamps when lesson data loads / story changes
    useEffect(() => { if (dataLesson?.id) addTimestamps(); }, [dataLesson]); // eslint-disable-line react-hooks/exhaustive-deps

    // Camera recording
    const cameraPreviewRef = useRef(null);
    const cameraStreamRef = useRef(null);
    const micStreamRef = useRef(null); // audio-only stream for mic whole-lesson recording

    // Ensure camera stream is assigned to video element after render
    useEffect(() => {
        if ((cameraReady || isRecording) && cameraStreamRef.current && cameraPreviewRef.current) {
            if (cameraPreviewRef.current.srcObject !== cameraStreamRef.current) {
                cameraPreviewRef.current.srcObject = cameraStreamRef.current;
                cameraPreviewRef.current.play().catch(err => console.log('Video play handled:', err));
            }
        }
    }, [cameraReady, isRecording]);

    // Phase 1: Prepare camera (show preview, setup recorder, but don't record yet)
    const prepareCamera = async () => {
        try {
            // Check camera permission
            if (navigator.permissions) {
                try {
                    const permStatus = await navigator.permissions.query({ name: 'camera' });
                    if (permStatus.state === 'denied') {
                        message.error('Quyền truy cập camera đã bị từ chối. Vui lòng vào Cài đặt trình duyệt → Quyền riêng tư → Camera để cho phép.');
                        return;
                    }
                } catch (e) { /* permissions API not supported for camera */ }
            }

            let stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: true
            });

            // Ensure we have an audio track to include in the recorder. Some devices/browsers
            // may not expose audio on the same track; if missing, request audio-only and merge.
            if (!stream.getAudioTracks() || stream.getAudioTracks().length === 0) {
                try {
                    const audioOnly = await navigator.mediaDevices.getUserMedia({ audio: true });
                    const combined = new MediaStream([
                        ...stream.getVideoTracks(),
                        ...audioOnly.getAudioTracks(),
                    ]);
                    stream = combined;
                } catch (e) {
                    console.warn('Could not get separate audio stream for camera:', e);
                }
            }

            cameraStreamRef.current = stream;
            if (cameraPreviewRef.current) {
                cameraPreviewRef.current.srcObject = stream;
                // Ensure video plays
                try {
                    await cameraPreviewRef.current.play();
                } catch (playErr) {
                    console.log('Video autoplay handled by browser');
                }
            }

            // Set up MediaRecorder (but don't start yet)
            const mimeTypes = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
            let selectedMime = '';
            for (const mime of mimeTypes) {
                if (MediaRecorder.isTypeSupported(mime)) { selectedMime = mime; break; }
            }
            const recorder = new MediaRecorder(stream, selectedMime ? { mimeType: selectedMime } : {});
            // attach chunks array to recorder so it's resettable per-record
            recorder.chunks = [];
            recorder.onstart = () => { recorder.chunks.length = 0; };
            recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) recorder.chunks.push(e.data); };
            recorder.onstop = () => {
                try {
                    const ext = selectedMime.includes('mp4') ? 'mp4' : 'webm';
                    const blob = new Blob(recorder.chunks, { type: selectedMime || 'video/webm' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `speaking_${dataLessonRef.current?.title || 'lesson'}.${ext}`;
                    a.click(); URL.revokeObjectURL(url);
                    message.success('Đã lưu bản ghi hình!');
                } catch (err) {
                    console.error('Error creating video blob:', err);
                    message.error('Không thể lưu bản ghi hình');
                }
            };
            setMediaRecorder(recorder);
            setCameraReady(true);
            message.success('Camera đã sẵn sàng! Bấm icon 📹 để bắt đầu ghi.');
        } catch (err) {
            console.error('Camera preparation error:', err);
            if (err.name === 'NotAllowedError') {
                message.error('Bạn đã từ chối quyền camera. Bấm 🔒 → Cho phép Camera → tải lại trang.');
            } else if (err.name === 'NotFoundError') {
                message.error('Không tìm thấy camera. Kiểm tra webcam đã kết nối chưa.');
            } else if (err.name === 'NotReadableError') {
                message.error('Camera đang dùng bởi ứng dụng khác.');
            } else {
                message.error('Không thể bật camera: ' + (err.message || err.name));
            }
        }
    };

    // Phase 2: Start actual recording
    const startActualRecording = async () => {
        if (!mediaRecorder || !cameraStreamRef.current) {
            // Attempt to prepare camera if recorder not present
            message.error('Camera chưa sẵn sàng! Bắt đầu chuẩn bị camera...');
            await prepareCamera();
            if (!cameraStreamRef.current) return;
        }
        try {
            // Ensure the recorder's stream contains an audio track. If missing, request audio and recreate recorder.
            let recorderToStart;
            try {
                const hasAudio = cameraStreamRef.current.getAudioTracks && cameraStreamRef.current.getAudioTracks().length > 0;
                if (!hasAudio) {
                    try {
                        const audioOnly = await navigator.mediaDevices.getUserMedia({ audio: true });
                        const combined = new MediaStream([
                            ...cameraStreamRef.current.getVideoTracks(),
                            ...audioOnly.getAudioTracks(),
                        ]);
                        cameraStreamRef.current = combined;
                        // recreate recorder with combined stream
                        const newRecorder = createRecorderForStream(combined);
                        setMediaRecorder(newRecorder);
                        // stop prior recorder if it's recording
                        if (mediaRecorder && mediaRecorder.state === 'recording') try { mediaRecorder.stop(); } catch (e) { console.warn(e); }
                        recorderToStart = newRecorder;
                    } catch (e) { console.warn('Could not obtain separate audio stream for camera:', e); }
                }
            } catch (e) { console.warn('Error ensuring audio track for recorder', e); }

            // Ensure lesson audio is NOT audible while recording (pause + mute)
            try {
                if (audioElemRef.current) {
                    try { audioElemRef.current.pause(); } catch (e) { /* noop */ }
                    try { audioElemRef.current.muted = true; } catch (e) { /* noop */ }
                    setAudioIsPlaying(false);
                }
            } catch (e) { console.warn('Failed to mute lesson audio during recording', e); }

            // Reset any previous chunks and start recorder
            const recorder = typeof recorderToStart !== 'undefined' ? recorderToStart : mediaRecorder;
            try { if (recorder.chunks) recorder.chunks.length = 0; } catch (e) {}
            recorder.start();
            setIsRecording(true);

            // For camera whole-lesson recording: do NOT trigger per-sentence mic starts.
            // We still clear/abort any ongoing SpeechRecognition and reset transcript state,
            // but advance lyrics automatically for the whole lesson (visual-only auto-advance).
            try { SpeechRecognition.getRecognition().abort(); } catch (e) {}
            resetTranscript();
            setRecordingAll(true);
            setRecordingIndex(-1);
            setIndexLyric(0);
            // schedule visual auto-advance from the beginning so lyrics progress sequentially
            scheduleAutoAdvance(0);
            message.success('Đang ghi hình...');
        } catch (err) {
            console.error('Recording start error:', err);
            message.error('Không thể bắt đầu ghi: ' + (err.message || err.name));
        }
    };

    // Close camera without recording
    const closeCamera = () => {
        if (cameraStreamRef.current) {
            cameraStreamRef.current.getTracks().forEach(t => t.stop());
            if (cameraPreviewRef.current) cameraPreviewRef.current.srcObject = null;
            cameraStreamRef.current = null;
        }
        setCameraReady(false);
        setMediaRecorder(null);
        message.info('Đã đóng camera.');
    };

    const stopScreenRecording = () => {
        // Stop camera recorder
        if (mediaRecorder) { 
            mediaRecorder.stop(); 
            setIsRecording(false); 
        }
        // Stop speech recognition → triggers batch scoring in [listening] useEffect
        stopContinuousRecording();
        // stop lesson audio playback
        try { if (audioElemRef.current) { audioElemRef.current.pause(); audioElemRef.current.src = ''; setAudioIsPlaying(false); } } catch (e) { console.warn(e); }
        // Clean up camera after a delay (to allow file download)
        setTimeout(() => {
            if (cameraStreamRef.current) {
                cameraStreamRef.current.getTracks().forEach(t => t.stop());
                if (cameraPreviewRef.current) cameraPreviewRef.current.srcObject = null;
                cameraStreamRef.current = null;
            }
            setCameraReady(false);
            setMediaRecorder(null);
        }, 1000);
    };

    useEffect(() => {
        if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }, [containerRef]);

    // Auto-scroll to active lyric when indexLyric changes during continuous recording
    // Do NOT auto-scroll when camera preview/recording is active — keep camera in view.
    useEffect(() => {
        if (!autoScroll) return;
        // If camera preview is visible or currently recording with camera, skip scrolling
        if (cameraReady || isRecording) return;
        if ((recordingAll || autoTranscript) && containerRef.current) {
            const el = containerRef.current.querySelector(`[data-lyric-index="${indexLyric}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [indexLyric, cameraReady, isRecording]);

    // Sync lyric to audio timestamp during camera recording
    // Only sync when lesson audio is actually playing to avoid conflicts with visual auto-advance
    useEffect(() => {
        if (!isRecording || !audioIsPlaying) return;
        const tsKey = toLowerCamelCase(storyRef.current);
        const tsArr = timestamps[tsKey] || [];
        if (!tsArr.length) return;
        let newIndex = 0;
        for (let i = 0; i < tsArr.length; i++) {
            if (audioCurrentTime >= tsArr[i]) newIndex = i;
            else break;
        }
        setIndexLyric(newIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioCurrentTime, isRecording, audioIsPlaying]);

    // Cleanup auto-advance timer on unmount
    useEffect(() => {
        return () => { clearAutoAdvance(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Stop auto-advance when recordingAll is turned off
    useEffect(() => {
        if (!recordingAll) clearAutoAdvance();
    }, [recordingAll]);

    useEffect(() => {
        if (recordingAll && listening) {
            scheduleAutoAdvance(indexLyricRef.current);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readingSpeed]);

    const fetchUserInfo = async () => {
        if (!cookies?.user?.token) return;
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/me`, {
                headers: {
                    'Authorization': `Bearer ${cookies?.user?.token}`
                }
            });
            setUserInfo(response.data);
        } catch (error) {
            console.error('Error fetching user info:', error);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { lesson(); if (cookies?.user) { getRecords(); userTopic(); fetchUserInfo(); } }, []);

    // Auto-transcript: automatically start recording when sentence changes
    useEffect(() => {
        if (!autoTranscript) return;
        if (recordingAll || isScoringAll) return; // Don't interfere with continuous mode
        if (recordingIndex !== -1) return; // Already recording
        if (micPermission === 'denied' || micPermission === 'unsupported') return;
        if (!arrayMain || arrayMain.length === 0) return;
        if (indexLyric >= arrayMain.length) return;

        // Small delay to allow UI update before starting recording
        const timer = setTimeout(() => {
            clickMic(indexLyric).catch(console.error);
        }, 300);

        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [indexLyric, autoTranscript]);

    useEffect(() => {
        // Only fire when listening transitions to false (speech engine stopped).
        if (listening) return;
        // Ignore if we've never actually started (prevents spurious fires on mount/unmount)
        if (!hasStartedRef.current) return;
        hasStartedRef.current = false;

        setRecordingIndex(-1);

        const currentTranscript = recordingAllRef.current ? (fullTranscriptRef.current || '').trim() : (transcriptRef.current || '').trim();
        const currentIndex      = indexLyricRef.current;
        const totalLines        = arrayMainRef.current.length;
        const isRecordingAll    = recordingAllRef.current;
        const lines             = arrayMainRef.current;
        const lesson            = dataLessonRef.current;
        const storyKey          = toLowerCamelCase(storyRef.current);

            if (isRecordingAll) {
            // ── CONTINUOUS MODE: batch-score all sentences from combined transcript ──
            if (!currentTranscript) {
                // nothing heard at all — silently reset without showing popup
                setRecordingAll(false);
                return;
            }

            const runBatchScore = () => {
                setIsScoringAll(true);
                // Preserve original positions: build array of { line, pos }
                try {
                    const linesWithPos = lines.map((l, idx) => ({ line: l, pos: idx }));
                    const validLines = linesWithPos.filter(l => l.line?.trim());
                    const aligned = alignTranscript(currentTranscript, validLines.map(v => v.line));

                    const posts = validLines.map((v, idx) => {
                        const chunk = aligned[idx]?.chunk?.join(' ') || currentTranscript;
                        const point = aligned[idx]?.point || 0;
                        const originalPos = v.pos;
                        return postRecord(lesson.id, originalPos, storyKey, chunk, point);
                    });

                    Promise.all(posts)
                        .then(() => getRecords())
                        .then(() => {
                            const scoredLines = validLines.map((v, idx) => ({
                                lyric: v.line,
                                pos: v.pos,
                                point: aligned[idx]?.point || 0,
                                spokenLine: aligned[idx]?.spokenLine,
                            }));
                            const avgPoint = Math.round(
                                scoredLines.reduce((s, l) => s + l.point, 0) / (scoredLines.length || 1)
                            );
                            setScoreResult({ avgPoint, lines: scoredLines, totalLines: validLines.length });
                            setShowScoreModal(true);
                        })
                        .catch((e) => {
                            console.error('Batch score error', e);
                            message.error('Có lỗi khi chấm điểm. Vui lòng thử lại.');
                        })
                        .finally(() => {
                            setIsScoringAll(false);
                            setRecordingAll(false);
                            fullTranscriptRef.current = '';
                            resetTranscript();
                        });
                } catch (e) {
                    console.error('Batch score sync error', e);
                    setIsScoringAll(false);
                    setRecordingAll(false);
                    fullTranscriptRef.current = '';
                    resetTranscript();
                }
            };

            runBatchScore();
        } else {
            // ── SINGLE SENTENCE MODE ──
            const wasManuallyStopped = userStoppedRef.current;
            userStoppedRef.current = false; // reset for next time

            if (currentTranscript !== '') {
                // Save record then auto-advance to next sentence (only if speech ended naturally)
                const advance = async () => {
                    await handleRecord(currentTranscript, currentIndex).catch(console.error);
                    resetTranscript();
                    // Auto-advance to next sentence only when speech engine ended naturally OR autoTranscript is enabled
                    if ((!wasManuallyStopped || autoTranscriptRef.current) && currentIndex < totalLines - 1) {
                        setTimeout(() => {
                            setIndexLyric(currentIndex + 1);
                        }, 600);
                    }
                };
                advance();
            }
            // If nothing heard: silently do nothing (no popup)
        }
    }, [listening]);

    useEffect(() => {
        const trackData = story === "Main Story" ? dataLesson?.mainStory
            : story === "Vocabulary" ? dataLesson?.vocabulary
            : story === 'Mini Story' ? dataLesson?.miniStory
            : story === 'POV' ? dataLesson?.POV : dataLesson?.comment;
        convertString(trackData);
        const opts = [];
        if (dataLesson?.mainStory?.trim()) opts.push('Main Story');
        if (dataLesson?.vocabulary?.trim()) opts.push('Vocabulary');
        if (dataLesson?.miniStory?.trim()) opts.push('Mini Story');
        if (dataLesson?.POV?.trim()) opts.push('POV');
        if (dataLesson?.comment?.trim()) opts.push('Comment');
        setOptions(opts);
        setRecordMain(records.filter(rec => +rec.lesson_id === +dataLesson.id && toCamelCase(rec.track) === story));
    }, [story, dataLesson, records]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (dataUserTopic?.lesson_id) {
            const vipExpireAt = userInfo?.vip_expire_at || cookies?.user?.vip_expire_at;
            if ((dayjsInstance(vipExpireAt)?.format("YYYY-MM-DD") > dayjsInstance(Date())?.format("YYYY-MM-DD")
                && vipExpireAt !== null)
                || +dataUserTopic?.lesson_id >= +dataLesson.id || +index === 0) {
            } else { navigate("/"); }
        }
        if (index === undefined || !cookies?.user) navigate("/login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cookies, userInfo]);

    // Stats (memoized)
    const totalLines = useMemo(() => arrayMain.length, [arrayMain]);
    const completedLines = useMemo(() => recordMain.filter(r => r.text !== 'skipped').length, [recordMain]);
    const skippedLines = useMemo(() => recordMain.filter(r => r.text === 'skipped').length, [recordMain]);
    const completionPercent = useMemo(() => totalLines > 0 ? Math.round((completedLines / totalLines) * 100) : 0, [totalLines, completedLines]);
    const avgScore = useMemo(() => {
        const scored = recordMain.filter(r => r.text !== 'skipped');
        return scored.length > 0 ? Math.round(scored.reduce((sum, r) => sum + (r.point || 0), 0) / scored.length) : 0;
    }, [recordMain]);

    const getLineStatus = (i) => {
        const rec = recordMain.find(r => +r.position === i);
        if (!rec) return 'pending';
        if (rec.text === 'skipped') return 'skipped';
        if (rec.point >= 80) return 'excellent';
        if (rec.point >= 50) return 'good';
        return 'needs-work';
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#0c1929] to-[#162d50] pb-28">
            {/* Hidden audio for camera-recording mode */}
            <audio
                ref={audioElemRef}
                onTimeUpdate={() => {
                    if (audioElemRef.current) setAudioCurrentTime(audioElemRef.current.currentTime * 1000);
                }}
                onEnded={() => { setAudioIsPlaying(false); }}
                preload="none"
            />
            {/* Header */}
            <div className="bg-[#0a1628]/80 backdrop-blur-md border-b border-white/10 sticky top-0 z-20">
                <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Breadcrumb
                        className="[&_.ant-breadcrumb-link]:text-blue-300 [&_.ant-breadcrumb-separator]:text-blue-400 [&_a]:text-blue-300 [&_a:hover]:text-white"
                        items={[
                            { title: <Link to="/"><HomeOutlined /> Trang chủ</Link> },
                            { title: <Link to="/topic">Topic</Link> },
                            { title: <Link to={"/lesson/" + dataLesson?.course_slug}>{dataLesson?.course_title}</Link> },
                            { title: <span className="text-white">{dataLesson?.title}</span> },
                        ]}
                    />
                    {isRecording && (
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 bg-red-500/20 px-3 py-1 rounded-full">
                                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                <span className="text-red-400 text-sm font-medium">
                                    {audioIsPlaying ? '🎬 Ghi hình + Audio' : '📹 Ghi hình'}
                                </span>
                            </div>
                            {audioIsPlaying && (
                                <span className="text-gray-400 text-sm">Câu {indexLyric + 1}/{totalLines}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Mic permission / browser support warning */}
            {(micPermission === 'denied' || micPermission === 'unsupported') && (
                <div className="max-w-screen-xl mx-auto px-4 pt-4">
                    <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4">
                        <WarningOutlined className="text-red-400 text-xl mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-red-400 font-semibold">
                                {micPermission === 'unsupported'
                                    ? 'Trình duyệt không hỗ trợ nhận dạng giọng nói'
                                    : 'Microphone bị chặn — không thể luyện nói'}
                            </p>
                            <p className="text-gray-400 text-sm mt-1">
                                {micPermission === 'unsupported'
                                    ? 'Vui lòng dùng Google Chrome hoặc Microsoft Edge để sử dụng tính năng này.'
                                    : 'Bấm vào biểu tượng 🔒 trên thanh địa chỉ → chọn Microphone → Cho phép → sau đó tải lại trang.'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-screen-xl mx-auto px-4 py-6">
                <h1 className="text-2xl md:text-3xl font-bold text-white text-center mb-6">{dataLesson?.title}</h1>

                {/* Track Selector */}
                <div className="flex justify-center mb-6">
                    <div className="bg-[#1a2d4a] rounded-xl p-1 inline-flex gap-1 flex-wrap justify-center">
                        {options.map(opt => (
                            <button key={opt} onClick={() => { setStory(opt); setIndexLyric(0); }}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                    story === opt ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                                        : 'text-blue-200 hover:text-white hover:bg-white/10'
                                }`}>
                                {opt}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Camera Preview - Center of screen when active */}
                {(cameraReady || isRecording) && (
                    <div className="mb-6 flex justify-center">
                        <div className="relative z-20 bg-[#1a2d4a] rounded-2xl p-4 shadow-2xl border border-white/10" style={{ maxWidth: '600px', width: '100%' }}>
                            <div className="relative">
                                <video ref={cameraPreviewRef} autoPlay muted playsInline
                                    className="w-full rounded-xl border-2 border-purple-500/50"
                                    style={{ maxHeight: '450px', objectFit: 'contain', background: '#000', transform: 'scaleX(-1)' }} />
                                
                                {/* Recording indicator */}
                                {isRecording && (
                                    <div className="absolute top-3 right-3 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold animate-pulse flex items-center gap-2">
                                        <span className="w-3 h-3 bg-white rounded-full animate-ping"></span>
                                        Đang ghi hình
                                    </div>
                                )}

                                {/* Mic indicator */}
                                {isRecording && (
                                    <div className="absolute top-3 left-3 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2">
                                        <span className="text-base">🎤</span>
                                        Mic {listening ? 'ON' : 'OFF'}
                                    </div>
                                )}

                                {/* Status text */}
                                {!isRecording && cameraReady && (
                                    <div className="absolute top-3 left-3 bg-purple-500/90 text-white px-4 py-2 rounded-full text-sm font-bold">
                                        📹 Camera sẵn sàng
                                    </div>
                                )}

                                {/* Transcript preview at bottom */}
                                {isRecording && (transcript || interimTranscript) && (
                                    <div className="absolute bottom-3 left-3 right-3 bg-black/70 text-white px-3 py-2 rounded-lg text-xs max-h-20 overflow-y-auto">
                                        <p className="text-gray-300 text-[10px] mb-1">Transcript:</p>
                                        <span className="text-white">{transcript}</span>
                                        <span className="text-gray-400 italic">{interimTranscript}</span>
                                    </div>
                                )}
                            </div>

                            {isRecording && (
                                <div className="mt-3 bg-[#0f1e35] rounded-lg px-4 py-2">
                                    <p className="text-blue-300 text-sm font-medium text-center">
                                        {Array.isArray(arrayMain) && arrayMain[indexLyric] ? (
                                            (() => {
                                                const cur = arrayMain[indexLyric];
                                                const liveText = ((transcript || '') + ' ' + (interimTranscript || '')).toLowerCase();
                                                const cleanedLive = liveText.replace(/[!@#$%^&*(),.?"':{}|<>\[\]\/\\]/g, ' ');
                                                const liveWordsSet = new Set(cleanedLive.split(/\s+/).filter(Boolean));
                                                return cur.split(' ').map((word, wi) => {
                                                    const cleanWord = word.replace(/[!@#$%^&*(),.?"':{}|<>\[\]\/\\]/g, '').toLowerCase();
                                                    const matched = cleanWord && liveWordsSet.has(cleanWord);
                                                    const cls = matched ? 'text-green-400' : 'text-blue-300';
                                                    return <span key={wi} className={`font-semibold ${cls}`}>{word} </span>;
                                                });
                                            })()
                                        ) : (
                                            `Câu ${indexLyric + 1}/${totalLines}`
                                        )}
                                    </p>
                                </div>
                            )}

                            {!isRecording && cameraReady && (
                                <div className="mt-3 bg-purple-500/10 rounded-lg px-4 py-3 border border-purple-500/20">
                                    <p className="text-purple-300 text-sm text-center leading-relaxed">
                                        {Array.isArray(arrayMain) && arrayMain[indexLyric] ? (
                                            <span className="font-semibold">{arrayMain[indexLyric]}</span>
                                        ) : (
                                            '👉 Bấm nút camera ở giữa bên dưới để bắt đầu ghi hình'
                                        )}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left: Lyric Lines Panel */}
                    <div className="lg:col-span-2">
                        <div className="bg-[#1a2d4a] rounded-2xl shadow-xl border border-white/5 overflow-hidden">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                                <h3 className="text-white font-semibold flex items-center gap-2">
                                    <SoundOutlined className="text-blue-400" /> Luyện nói
                                </h3>
                                <span className="text-blue-300 text-sm bg-blue-500/10 px-3 py-1 rounded-full">
                                    Câu {indexLyric + 1}/{totalLines}
                                </span>
                            </div>

                            <div className="max-h-[500px] overflow-y-auto p-4" ref={containerRef}>
                                {arrayMain.map((e, i) => {
                                    const rec = recordMain.find(r => +r.position === i);
                                    const status = getLineStatus(i);
                                    const isActive = indexLyric === i;
                                    const isRecLine = recordingIndex === i;
                                    // In continuous mode: highlight words matched so far via interimTranscript
                                    const liveText = recordingAll ? (transcript + ' ' + interimTranscript) : '';

                                    return (
                                        <div key={i}
                                            data-lyric-index={i}
                                            className={`rounded-xl mb-2 transition-all cursor-pointer border-l-4 ${
                                                isActive ? 'bg-blue-500/15 border-blue-400' :
                                                status === 'excellent' ? 'bg-green-500/5 border-green-500/50 hover:bg-green-500/10' :
                                                status === 'good' ? 'bg-yellow-500/5 border-yellow-500/50 hover:bg-yellow-500/10' :
                                                status === 'needs-work' ? 'bg-red-500/5 border-red-500/50 hover:bg-red-500/10' :
                                                'border-transparent hover:bg-white/5'
                                            }`}
                                            onClick={() => setIndexLyric(i)}
                                        >
                                            <div className="flex items-center justify-between px-4 py-3 gap-3">
                                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                                    <span className="text-xs text-gray-500 mt-1 flex-shrink-0 w-6 text-right">{i + 1}</span>
                                                    <div className="flex flex-wrap gap-0.5">
                                                        {e.split(' ').map((word, wi) => {
                                                            const cleanWord = word.replace(/[!@#$%^&*(),.?"":{}|<>""]/g, '').toLowerCase();
                                                            // Highlight logic: only show green/red after scoring is complete
                                                            let colorClass = isActive ? 'text-blue-200' : 'text-gray-400';
                                                            if (rec && rec.point !== undefined) {
                                                                // Already scored - show accurate green/red highlighting
                                                                if (rec.spokenLine && Array.isArray(rec.spokenLine)) {
                                                                    // Use spokenLine from alignTranscript for accurate highlighting
                                                                    const spokenWord = rec.spokenLine.find(sw => sw.word.toLowerCase() === cleanWord);
                                                                    colorClass = spokenWord?.matched ? 'text-green-400' : 'text-red-400';
                                                                } else if (rec.text) {
                                                                    const recWords = rec.text.replace(/[!@#$%^&*(),.?"":{}|<>""]/g, '').toLowerCase().split(' ');
                                                                    colorClass = recWords.includes(cleanWord) ? 'text-green-400' : 'text-red-400';
                                                                }
                                                            } else if (isRecLine) {
                                                                // Currently recording this line - show orange
                                                                colorClass = 'text-orange-400';
                                                            }
                                                            return (
                                                                <span key={wi} className={`font-semibold ${colorClass}`} style={{ fontSize: `${subtitleFontSize}px` }}>{word} </span>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    {rec && (
                                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                                            rec.text === 'skipped' ? 'bg-gray-500/20 text-gray-400' :
                                                            rec.point >= 80 ? 'bg-green-500/20 text-green-400' :
                                                            rec.point >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                                                            'bg-red-500/20 text-red-400'
                                                        }`}>{rec.text === 'skipped' ? 'Skip' : `${rec.point}%`}</span>
                                                    )}
                                                    {/* Skip button - only show when not recorded yet */}
                                                    {!rec && !recordingAll && !isScoringAll && recordingIndex === -1 && (
                                                        <Tooltip title="Bỏ qua câu này (khó/không hiểu)">
                                                            <button
                                                                onClick={(ev) => {
                                                                    ev.stopPropagation();
                                                                    skipSentence(i);
                                                                }}
                                                                className="w-9 h-9 rounded-full flex items-center justify-center transition-all bg-white/5 text-gray-400 hover:bg-orange-500/20 hover:text-orange-400"
                                                            >
                                                                <CloseCircleOutlined />
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                    {/* Per-sentence mic: disabled during continuous mode */}
                                                    <button
                                                        disabled={recordingAll || isScoringAll || (recordingIndex > -1 && recordingIndex !== i) || micPermission === 'denied' || micPermission === 'unsupported'}
                                                        onClick={(ev) => { ev.stopPropagation(); handlePerSentenceRecord(i); }}
                                                        className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                                                            isRecLine ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/30'
                                                                : (recordingAll || isScoringAll || (recordingIndex > -1 && recordingIndex !== i) || micPermission === 'denied' || micPermission === 'unsupported') ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                                                : 'bg-white/10 text-blue-300 hover:bg-blue-500 hover:text-white'
                                                        }`}
                                                    >
                                                        {isRecLine ? (isRecording ? <VideoCameraOutlined /> : <AudioFilled />) :
                                                            (recordingAll || isScoringAll) ? <AudioMutedOutlined /> :
                                                            recordingIndex > -1 && recordingIndex !== i ? <AudioMutedOutlined /> :
                                                            isRecording ? <VideoCameraOutlined /> : <AudioOutlined />}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Translation and IPA */}
                                            {/* IPA + Translation block (show when toggled) */}
                                            <div className="px-4 pb-2">
                                                {showIPA && ipaData[i] && (
                                                    <div className="mt-1 text-cyan-400/70 font-mono text-xs">
                                                        {ipaData[i]}
                                                    </div>
                                                )}
                                                {showTranslation && (
                                                    <div className="mt-1 text-yellow-300/70 italic" style={{ fontSize: `${translationFontSize}px` }}>
                                                        {translationLoading ? (
                                                            <span className="opacity-40 animate-pulse">Đang dịch...</span>
                                                        ) : (
                                                            translations[i] || <span className="opacity-30">—</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* User's spoken text - always visible */}
                                            {isActive && rec?.text && rec.text !== 'skipped' && !recordingAll && (
                                                <div className="px-4 pb-3 pt-0">
                                                    <div className="text-gray-400 text-xs mb-2">Lời đã nói</div>
                                                    <div className="bg-[#0f1e35] rounded-lg px-4 py-2">
                                                        <p className="text-blue-300 font-medium">{rec.text}</p>
                                                    </div>
                                                </div>
                                            )}
                                            {/* Skipped message */}
                                            {isActive && rec?.text === 'skipped' && !recordingAll && (
                                                <div className="px-4 pb-3 pt-0">
                                                    <div className="bg-orange-500/10 rounded-lg px-4 py-2 mt-1 border border-orange-500/20">
                                                        <p className="text-orange-400 text-xs">⚠️ Câu này đã được bỏ qua (khó/không hiểu)</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Right: Stats & Tools */}
                    <div className="lg:col-span-1 space-y-4">
                        {/* Progress Stats */}
                        <div className="bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
                                <TrophyOutlined className="text-yellow-400 mr-2" />Tiến độ
                            </h3>
                            <div className="flex items-center justify-center gap-6 mb-4">
                                <Progress
                                    type="circle" size={80} percent={completionPercent}
                                    strokeColor={{ '0%': '#3b82f6', '100%': '#10b981' }}
                                    trailColor="#1a2d4a"
                                    format={() => <span className="text-white text-sm font-bold">{completionPercent}%</span>}
                                />
                                <div>
                                    <p className="text-gray-400 text-xs">Hoàn thành</p>
                                    <p className="text-white text-lg font-bold">{completedLines}/{totalLines} câu</p>
                                    <p className="text-gray-400 text-xs mt-2">Điểm trung bình</p>
                                    <p className={`text-lg font-bold ${avgScore >= 80 ? 'text-green-400' : avgScore >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                                        {avgScore}%
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="bg-green-500/10 rounded-lg py-2">
                                    <p className="text-green-400 font-bold text-lg">
                                        {recordMain.filter(r => r.point >= 80).length}
                                    </p>
                                    <p className="text-gray-400 text-xs">Xuất sắc</p>
                                </div>
                                <div className="bg-yellow-500/10 rounded-lg py-2">
                                    <p className="text-yellow-400 font-bold text-lg">
                                        {recordMain.filter(r => r.point >= 50 && r.point < 80).length}
                                    </p>
                                    <p className="text-gray-400 text-xs">Khá</p>
                                </div>
                                <div className="bg-red-500/10 rounded-lg py-2">
                                    <p className="text-red-400 font-bold text-lg">
                                        {recordMain.filter(r => r.point < 50).length}
                                    </p>
                                    <p className="text-gray-400 text-xs">Cần cải thiện</p>
                                </div>
                            </div>
                        </div>

                        {/* Tools */}
                        <div className="bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Công cụ</h3>
                            <div className="grid grid-cols-2 gap-3">
                                {/* Download audio */}
                                <Tooltip title="Tải file âm thanh">
                                    <button onClick={downloadAudio}
                                        className="flex items-center gap-2 bg-green-500/10 text-green-400 px-4 py-3 rounded-xl hover:bg-green-500/20 transition-all text-sm font-medium w-full">
                                        <DownloadOutlined className="text-lg" /> Tải audio
                                    </button>
                                </Tooltip>
                                {/* Camera recording */}
                                {!cameraReady && !isRecording && (
                                    <Tooltip title="Chuẩn bị camera để ghi hình">
                                        <button onClick={prepareCamera}
                                            className="flex items-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-purple-500/10 text-purple-400 hover:bg-purple-500/20">
                                            <VideoCameraOutlined className="text-lg" /> Ghi hình
                                        </button>
                                    </Tooltip>
                                )}
                                {cameraReady && !isRecording && (
                                    <Tooltip title="Đóng camera">
                                        <button onClick={closeCamera}
                                            className="flex items-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-gray-500/10 text-gray-400 hover:bg-gray-500/20">
                                            <CloseCircleOutlined className="text-lg" /> Đóng camera
                                        </button>
                                    </Tooltip>
                                )}
                                {/* Navigate to Listening */}
                                <Tooltip title="Chuyển sang trang bài học">
                                    <button
                                        onClick={() => navigate(`/lesson/listening/${slug}`, { state: { index } })}
                                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
                                    >
                                        <span className="text-lg">📚</span> Bài học
                                    </button>
                                </Tooltip>
                                {/* Navigate to Drill Mode */}
                                <Tooltip title="Luyện nghe từng câu — nghe audio từng câu, có Prev/Next">
                                    <button
                                        onClick={() => navigate(`/lesson/listening/${slug}`, { state: { index, openDrillMode: true } })}
                                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20"
                                    >
                                        <span className="text-lg">🎯</span> Luyện nghe
                                    </button>
                                </Tooltip>
                                {/* Settings toggle */}
                                <button onClick={() => setShowSettings(v => !v)}
                                    className={`col-span-2 flex items-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full ${
                                        showSettings
                                            ? 'bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30'
                                            : 'bg-white/5 text-gray-300 hover:bg-white/10'
                                    }`}>
                                    <SettingOutlined className="text-lg" /> Cài đặt
                                </button>
                            </div>
                            {/* Camera preview removed from sidebar - now shows in center of screen */}
                        </div>

                        {/* Settings Panel */}
                        {showSettings && (
                            <div className="relative z-50 bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                                <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Cài đặt</h3>
                                <div className="space-y-4">
                                    {/* Reading speed */}
                                    <div>
                                        <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                                            <StepForwardOutlined /> Tốc độ đọc lyric
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {[0.5, 0.75, 1, 1.25, 1.5].map(s => (
                                                <button key={s} onClick={() => setReadingSpeed(s)}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                        readingSpeed === s
                                                            ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                                                            : 'bg-white/10 text-gray-300 hover:bg-white/20'
                                                    }`}>
                                                    {s}x
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-white/10"></div>

                                    {/* Auto Scroll Toggle */}
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-400 text-xs flex items-center gap-1">
                                            <RetweetOutlined /> Tự động cuộn
                                        </p>
                                        <Switch
                                            size="small"
                                            checked={autoScroll}
                                            onChange={v => setAutoScroll(v)}
                                            className={autoScroll ? '!bg-blue-500' : ''}
                                        />
                                    </div>

                                    {/* Auto Transcript Toggle */}
                                    <div className="flex items-center justify-between">
                                        <Tooltip title="Tự động bắt đầu ghi âm khi chuyển sang câu mới và tự động chuyển câu tiếp theo sau khi ghi xong">
                                            <p className="text-gray-400 text-xs flex items-center gap-1 cursor-help">
                                                <AudioOutlined /> Tự động chấm điểm từng câu
                                            </p>
                                        </Tooltip>
                                        <Switch
                                            size="small"
                                            checked={autoTranscript}
                                            onChange={v => setAutoTranscript(v)}
                                            className={autoTranscript ? '!bg-blue-500' : ''}
                                        />
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-white/10"></div>

                                    {/* Subtitle font size */}
                                    <div>
                                        <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                                            <FontSizeOutlined /> Cỡ chữ phụ đề: {subtitleFontSize}px
                                        </p>
                                        <Slider min={12} max={28} value={subtitleFontSize} onChange={v => setSubtitleFontSize(v)}
                                            className="[&_.ant-slider-track]:bg-blue-500 [&_.ant-slider-handle]:border-blue-500" />
                                    </div>

                                    {/* Translation font size */}
                                    <div>
                                        <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                                            <FontSizeOutlined /> Cỡ chữ bản dịch: {translationFontSize}px
                                        </p>
                                        <Slider min={10} max={24} value={translationFontSize} onChange={v => setTranslationFontSize(v)}
                                            className="[&_.ant-slider-track]:bg-blue-500 [&_.ant-slider-handle]:border-blue-500" />
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-white/10"></div>

                                    {/* Show Translation Toggle */}
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-400 text-xs flex items-center gap-1">
                                            <EyeOutlined /> Hiển thị bản dịch
                                        </p>
                                        <Switch
                                            size="small"
                                            checked={showTranslation}
                                            onChange={v => setShowTranslation(v)}
                                            className={showTranslation ? '!bg-blue-500' : ''}
                                        />
                                    </div>

                                    {/* Show IPA Toggle */}
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-400 text-xs flex items-center gap-1">
                                            <EyeOutlined /> Hiển thị phiên âm IPA
                                        </p>
                                        <Switch
                                            size="small"
                                            checked={showIPA}
                                            onChange={v => setShowIPA(v)}
                                            className={showIPA ? '!bg-blue-500' : ''}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tips */}
                        <div className="bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                            <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">
                                <FireOutlined className="text-orange-400 mr-2" />Mẹo luyện nói
                            </h3>
                            <ul className="space-y-2 text-gray-300 text-sm">
                                <li className="flex items-start gap-2">
                                    <CheckCircleOutlined className="text-green-400 mt-0.5 flex-shrink-0" />
                                    <span>Nói to và rõ ràng, giữ tốc độ vừa phải</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircleOutlined className="text-green-400 mt-0.5 flex-shrink-0" />
                                    <span>Bấm nút 🎙 nhỏ để thu âm <strong>từng câu</strong></span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircleOutlined className="text-blue-400 mt-0.5 flex-shrink-0" />
                                    <span>Bấm nút 🎙 <strong>lớn bên dưới</strong> để ghi âm <strong>xuyên suốt toàn bài</strong> — đọc hết rồi nhấn Dừng để chấm điểm tất cả</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircleOutlined className="text-green-400 mt-0.5 flex-shrink-0" />
                                    <span>Từ <span className="text-green-400">xanh</span> = đúng, <span className="text-red-400">đỏ</span> = cần sửa</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircleOutlined className="text-green-400 mt-0.5 flex-shrink-0" />
                                    <span>Lặp lại nhiều lần để đạt điểm cao hơn</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Control Bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-[#0a1628]/95 backdrop-blur-xl border-t border-white/10 z-30">
                <div className="max-w-screen-xl mx-auto px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                        {/* Track Info */}
                        <div className="hidden sm:block min-w-0 flex-shrink-0">
                            <p className="text-white text-sm font-semibold truncate max-w-[200px]">{dataLesson?.title}</p>
                            <p className="text-gray-400 text-xs">{story} • Câu {indexLyric + 1}/{totalLines}</p>
                            {recordingAll && (
                                <div className="flex gap-1 mt-1">
                                    {[0.5, 0.75, 1, 1.25, 1.5].map(s => (
                                        <button key={s}
                                            onClick={() => setReadingSpeed(s)}
                                            className={`px-1.5 py-0.5 rounded text-[10px] font-bold transition-all ${
                                                readingSpeed === s ? 'bg-blue-500 text-white' : 'bg-white/10 text-gray-400 hover:bg-white/20'
                                            }`}>
                                            {s}x
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Navigation + Record */}
                        <div className="flex items-center gap-2 sm:gap-4">
                            <button onClick={() => indexLyric > 0 && setIndexLyric(indexLyric - 1)}
                                className="text-gray-300 hover:text-white p-2 transition-colors">
                                <StepBackwardOutlined className="text-xl" />
                            </button>

                            <Tooltip title={
                                cameraReady || isRecording ? (
                                    isRecording ? '🛑 Dừng ghi & chấm điểm' : '📹 Bắt đầu ghi hình'
                                ) : (
                                    micPermission === 'denied' ? 'Microphone bị chặn — kiểm tra quyền trình duyệt' :
                                    micPermission === 'unsupported' ? 'Trình duyệt không hỗ trợ — dùng Chrome/Edge' :
                                    isScoringAll ? 'Đang chấm điểm...' :
                                    listening && recordingAll ? 'Dừng và chấm điểm toàn bài' :
                                    'Ghi âm xuyên suốt toàn bài rồi chấm điểm'
                                )
                            }>
                                <button
                                    disabled={isScoringAll || (isRecording && !cameraReady) || (!cameraReady && (micPermission === 'denied' || micPermission === 'unsupported'))}
                                    onClick={() => {
                                        if (cameraReady || isRecording) {
                                            // Camera mode
                                            if (isRecording) {
                                                stopScreenRecording();
                                            } else {
                                                startActualRecording();
                                            }
                                        } else {
                                            // Mic mode
                                            if (isScoringAll) return;
                                            if (listening && recordingAll) {
                                                stopContinuousRecording();
                                            } else if (!listening) {
                                                startContinuousRecording();
                                            }
                                        }
                                    }}
                                    className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105 ${
                                        isScoringAll ? 'bg-gray-600 shadow-gray-500/20 cursor-not-allowed' :
                                        cameraReady && !isRecording ? 'bg-purple-600 shadow-purple-500/50' :
                                        isRecording ? 'bg-red-500 shadow-red-500/30 animate-pulse' :
                                        (micPermission === 'denied' || micPermission === 'unsupported') ? 'bg-gray-600 cursor-not-allowed' :
                                        listening && recordingAll ? 'bg-red-500 shadow-red-500/30 animate-pulse' :
                                        'bg-blue-500 shadow-blue-500/30'
                                    }`}>
                                    {isScoringAll ? <LoadingOutlined className="text-2xl text-white" spin /> : 
                                     (cameraReady || isRecording) ? <VideoCameraOutlined className="text-2xl text-white" /> : 
                                     <AudioFilled className="text-2xl text-white" />}
                                </button>
                            </Tooltip>

                            <button onClick={() => indexLyric < arrayMain.length - 1 && setIndexLyric(indexLyric + 1)}
                                className="text-gray-300 hover:text-white p-2 transition-colors">
                                <StepForwardOutlined className="text-xl" />
                            </button>
                        </div>

                        {/* Status */}
                        <div className="flex items-center gap-3">
                            {isScoringAll && (
                                <div className="hidden sm:flex items-center gap-2 bg-yellow-500/20 px-3 py-1.5 rounded-full">
                                    <LoadingOutlined className="text-yellow-400" spin />
                                    <span className="text-yellow-400 text-sm font-medium">Đang chấm điểm...</span>
                                </div>
                            )}
                            {listening && recordingAll && !isScoringAll && (
                                <div className="hidden sm:flex flex-col items-start gap-1 max-w-[200px]">
                                    <div className="flex items-center gap-2 bg-red-500/20 px-3 py-1 rounded-full">
                                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                        <span className="text-red-400 text-sm font-medium">Đang ghi toàn bài...</span>
                                    </div>
                                    {(transcript || interimTranscript) && (
                                        <p className="text-gray-400 text-xs truncate max-w-[200px] px-1">
                                            {(transcript + ' ' + interimTranscript).trim().slice(-60)}
                                        </p>
                                    )}
                                </div>
                            )}
                            {listening && !recordingAll && (
                                <div className="hidden sm:flex items-center gap-2 bg-red-500/20 px-3 py-1.5 rounded-full">
                                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                    <span className="text-red-400 text-sm font-medium">Đang nghe...</span>
                                </div>
                            )}
                            <span className={`text-sm font-bold px-3 py-1.5 rounded-full ${
                                completionPercent >= 80 ? 'bg-green-500/20 text-green-400' :
                                completionPercent >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-blue-500/20 text-blue-400'
                            }`}>
                                {completionPercent}%
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Removed: retry modal for no-voice — replaced by automatic retry behavior */}

            {/* ── Score Results Modal ─────────────────────────────────────── */}
            <Modal
                open={showScoreModal}
                onCancel={() => setShowScoreModal(false)}
                footer={[
                    <Button key="close" onClick={() => setShowScoreModal(false)}
                        className="!bg-blue-500 !text-white !border-blue-500 hover:!bg-blue-600">
                        Đóng
                    </Button>
                ]}
                title={<span className="text-white font-bold text-lg">🎤 Kết quả luyện nói</span>}
                className="[&_.ant-modal-content]:bg-[#1a2d4a] [&_.ant-modal-content]:border [&_.ant-modal-content]:border-white/10 [&_.ant-modal-header]:bg-transparent [&_.ant-modal-title]:text-white [&_.ant-modal-close]:text-gray-400 [&_.ant-modal-footer]:bg-transparent [&_.ant-modal-footer]:border-t [&_.ant-modal-footer]:border-white/10"
                width={600}
            >
                {scoreResult && (
                    <div>
                        {/* Overall score */}
                        <div className="flex items-center justify-center gap-8 mb-5 py-5 bg-[#0f1e35] rounded-xl border border-white/5">
                            <Progress
                                type="circle"
                                size={88}
                                percent={scoreResult.avgPoint}
                                strokeColor={
                                    scoreResult.avgPoint >= 80 ? '#22c55e'
                                    : scoreResult.avgPoint >= 50 ? '#f59e0b'
                                    : '#ef4444'
                                }
                                trailColor="rgba(255,255,255,0.1)"
                                format={p => <span className="text-white font-bold text-lg">{p}%</span>}
                            />
                            <div className="text-center">
                                <p className="text-gray-400 text-sm mb-1">Điểm trung bình</p>
                                <p className={`text-3xl font-bold mb-1 ${
                                    scoreResult.avgPoint >= 80 ? 'text-green-400'
                                    : scoreResult.avgPoint >= 50 ? 'text-yellow-400'
                                    : 'text-red-400'
                                }`}>
                                    {scoreResult.avgPoint}%
                                </p>
                                <p className="text-gray-400 text-xs">{scoreResult.totalLines} câu</p>
                            </div>
                        </div>

                        {/* Per-line results */}
                        <div className="max-h-[320px] overflow-y-auto space-y-2 pr-1
                            [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full">
                            {scoreResult.lines.map((line, i) => (
                                <div key={i} className="bg-[#0f1e35] rounded-lg px-4 py-3 border border-white/5">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-gray-400 text-xs font-medium">Câu {i + 1}</span>
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                            line.point >= 80 ? 'bg-green-500/20 text-green-400'
                                            : line.point >= 50 ? 'bg-yellow-500/20 text-yellow-400'
                                            : 'bg-red-500/20 text-red-400'
                                        }`}>
                                            {line.point}%
                                        </span>
                                    </div>
                                    <p className="text-gray-300 text-xs mb-1.5 leading-relaxed">{line.lyric}</p>
                                    <div className="flex flex-wrap gap-x-1 gap-y-0.5 bg-black/20 rounded px-2 py-1.5">
                                        <span className="text-[10px] text-gray-500 mr-0.5 self-center">🎤</span>
                                        {line.spokenLine?.length > 0
                                            ? line.spokenLine.map(({ word, matched }, wi) => (
                                                <span key={wi} className={`font-semibold text-sm ${matched ? 'text-green-400' : 'text-red-400'}`}>
                                                    {word}
                                                </span>
                                            ))
                                            : <span className="text-gray-500 italic text-xs">Không nhận được giọng</span>
                                        }
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="text-gray-500 text-xs mt-3 text-center">
                            <span className="text-green-400 font-semibold">Xanh</span> = đúng &nbsp;·&nbsp;
                            <span className="text-red-400 font-semibold">Đỏ</span> = sai / thiếu
                        </p>
                    </div>
                )}
            </Modal>
        </div>
    );
};
